import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, Heart, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLifestyleGallery } from '@/hooks/useLifestyleGallery';
import { Skeleton } from '@/components/ui/skeleton';

interface GalleryImage {
  id: string;
  src: string;
  title: string;
  category: string;
  likes: number;
  views: number;
}

const InteractivePhotoGallery = () => {
  const { data: lifestyleData, isLoading } = useLifestyleGallery();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleImages, setVisibleImages] = useState(3);

  // Transform database data to match the expected format
  const galleryImages: GalleryImage[] = lifestyleData?.map(item => ({
    id: item.id,
    src: item.image_url,
    title: item.title,
    category: item.category,
    likes: item.likes,
    views: item.views
  })) || [];

  useEffect(() => {
    if (!isAutoPlaying || galleryImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, galleryImages.length]);

  const handleImageClick = (index: number) => {
    if (galleryImages.length === 0) return;
    setCurrentImageIndex(index);
    setIsAutoPlaying(false);
  };

  const getVisibleImages = () => {
    const images = [];
    for (let i = 0; i < visibleImages; i++) {
      const index = (currentImageIndex + i) % galleryImages.length;
      images.push({ ...galleryImages[index], displayIndex: i });
    }
    return images;
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-gradient-to-r from-background via-muted/5 to-background overflow-hidden">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <Skeleton className="h-8 w-32 mx-auto mb-4" />
            <Skeleton className="h-12 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="relative max-w-7xl mx-auto">
            <div className="flex gap-4 justify-center">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className={`${i === 1 ? 'w-96 h-64' : 'w-72 h-48'} rounded-2xl`} />
              ))}
            </div>
            <div className="flex justify-center mt-8 gap-2">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="w-2 h-2 rounded-full" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!galleryImages || galleryImages.length === 0) {
    return null;
  }

  return (
    <section 
      className="py-16 bg-gradient-to-r from-background via-muted/5 to-background overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-4">
            <Eye className="h-4 w-4" />
            <span className="text-sm font-medium">Gallery</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Lifestyle Collection</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Discover how our pieces complement every moment of luxury living
          </p>
        </motion.div>

        {/* Main Gallery Display */}
        <div className="relative max-w-7xl mx-auto">
          <div className="flex gap-4 overflow-hidden justify-center">
            {getVisibleImages().map((image, index) => {
              const isCenter = index === 1;
              const isEdge = index === 0 || index === visibleImages - 1;
              
              return (
                <motion.div
                  key={`${image.id}-${image.displayIndex}`}
                  className={`relative cursor-pointer group ${
                    isCenter ? 'w-96 h-64' : 'w-72 h-48'
                  } ${isEdge ? 'opacity-60' : 'opacity-100'}`}
                  onClick={() => handleImageClick((currentImageIndex + index) % galleryImages.length)}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: isEdge ? 0.6 : 1, 
                    scale: isCenter ? 1 : 0.9,
                    y: isCenter ? 0 : 20
                  }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  whileHover={{ 
                    scale: isCenter ? 1.05 : 0.95,
                    opacity: 1,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="font-semibold text-lg mb-1">{image.title}</h4>
                        <p className="text-sm opacity-90 mb-3">{image.category}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                              <Heart className="h-4 w-4" />
                              {image.likes}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              {image.views}
                            </span>
                          </div>
                          
                          <Button size="sm" variant="ghost" className="text-white hover:bg-white/20">
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Center indicator */}
                    {isCenter && (
                      <motion.div
                        className="absolute top-4 right-4 w-3 h-3 bg-primary rounded-full"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3 }}
                      />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Gallery Controls */}
          <div className="flex justify-center mt-8 gap-2">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => handleImageClick(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex
                    ? 'bg-primary w-6'
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>

          {/* Auto-play indicator */}
          <motion.div
            className="mt-4 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className={`w-2 h-2 rounded-full ${isAutoPlaying ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
              {isAutoPlaying ? 'Auto-playing' : 'Paused'}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InteractivePhotoGallery;